/* Single source of truth for landing-page defaults on the frontend.
 *
 * Mirrors `/app/backend/app/landing_defaults.py`. The backend is
 * authoritative — this file is the FALLBACK used when `/api/landing`
 * fails (e.g. the page is server-rendered to an HTML snapshot tool
 * that can't reach the API). Keep the two in sync.
 */

export const FONT_STACKS = {
  system:       "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  inter:        "'Inter', system-ui, -apple-system, sans-serif",
  georgia:      "Georgia, 'Times New Roman', Times, serif",
  merriweather: "'Merriweather', Georgia, serif",
  playfair:     "'Playfair Display', Georgia, serif",
  ibm_plex:     "'IBM Plex Sans', system-ui, sans-serif",
};

export const FONT_LABELS = {
  system:       'System UI',
  inter:        'Inter',
  georgia:      'Georgia',
  merriweather: 'Merriweather',
  playfair:     'Playfair Display',
  ibm_plex:     'IBM Plex Sans',
};

// Google Fonts that need a CSS link injected (system + georgia are
// already on every device, so they're not in the list).
export const FONT_GFONT_URLS = {
  inter:        'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  merriweather: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
  playfair:     'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap',
  ibm_plex:     'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap',
};

export const LANDING_CONTENT_DEFAULTS = {
  brand_name:           'Crown & Rose',
  top_email_signin:     'Email sign-in',
  top_google_signin:    'Google',
  top_open_app_label:   'Open The Loom',

  hero_eyebrow:         'Creators now have a full-featured business dashboard for revenue, marketing, and fan management to sell their works.',
  hero_headline_part1:  'We are where Indie writers own their business.',
  hero_headline_accent: '',
  // iter-p3-x — eyebrow block above the hero headline.
  hero_pre_headline: 'Home for Indie Authors',
  // iter-p3-t — Second hero tagline block; sits directly below the
  // typewriter pane as a large, standalone flag line.
  hero_business_tagline: 'Your Indie writing business, your way.',
  // iter-p3-x — Royalty promise block under the tagline.
  hero_royalty_line: "Authors' finished work can be sold at the online ebook store, where they get up to 85% of the sales.",
  mei_intro_video_url:    '/assets/mei-intro-video.mp4',
  mei_intro_poster_url:   '/assets/mei-intro-poster.jpeg',
  mei_intro_title:        '',
  mei_intro_captions_url: '',
  // iter-p3-u — matching bookstore hero tagline for brand parity.
  bookstore_business_tagline: 'Your Indie writing business, your way.',
  hero_subline:
    'You write, design, and even translate your novels so that you write for a global community of Indie readers.',
  // 2026-02 · hero body rewrite — global-market pitch. Three
  // stacked paragraphs directly under the typewriter subline, the
  // last one flagged large so it lands as the promise line.
  hero_paragraph_3:
    "Instead of hoping for smaller regional sales and a shot at the New York Best Seller's List, look to global markets for your work — and a Global Best Seller status.",
  hero_paragraph_4_large:
    'Write, design, and even translate your novels so your stories reach a global community of indie readers.',
  // iter-p3-r — Craft Multiplier + FAQ CTA copy.
  craft_multiplier_headline: 'AI assist for copywriters and creatives.',
  craft_multiplier_body:
    'Writers are assisted by AI for copywriter-able creations; creatives may also find the Craft Multiplier useful for content in the bookstore under AI offerings.',
  faq_cta_label: 'Read the full FAQ →',
  faq_cta_body:
    "New here? Get every question about pricing, copyright, translation, and the Craft Multiplier answered on one page.",
  // iter-p3-s — Trust & Integrity + Reader-Building badges.
  trust_badge_title:  'Trust & Integrity',
  trust_badge_body:
    'If a novel is written by a person or produced with our Content Multiplier, it will be clearly marked on the shelf and inside every book\'s provenance record — no exceptions, no fine print.',
  reader_badge_title: 'Building Trust — One Reader at a Time',
  reader_badge_body:
    '85% of every sale goes back to the writer from our bookstore. Every book carries a downloadable Editing Record so readers can see exactly how it was made. That is how a bookstore earns loyalty — one honest transaction at a time.',
  hero_cta_primary:     'Open The Loom',
  hero_cta_secondary:   'Visit the Book Store',
  hero_safety_note:     'Secure sign-in · Stripe-managed billing · Your manuscript stays yours.',

  hero_card_chapter:    'Chapter 14',
  hero_card_status:     'Editing',
  hero_card_quote:
    'The lighthouse beam swept the cliffs again, and Eleanor thought, only for a ' +
    'heartbeat, that the figure on the shingle was waving back.',
  hero_card_mode_1:     'Tighten',
  hero_card_mode_2:     'Sharpen',
  hero_card_mode_3:     'Voice',

  pw_card_eyebrow:      'Where Drafts Become Tapestries',
  pw_card_title:        'The Loom',
  pw_card_body:
    'Multi-model AI (Claude, GPT, Gemini) with chapter-aware context, version history, ' +
    'character bibles, plagiarism scans, and DOCX / PDF export.',
  pw_card_cta:          'Open The Loom',

  bs_card_eyebrow:      'For readers',
  bs_card_status:       'Open now',
  bs_card_title:        'Book Store',
  bs_card_body:
    'Shop our bookstore now. Check out our Contemporary Romance and ' +
    'our newest Dark Romance selections.',
  bs_card_cta:          'Shop the Book Store',

  footer_text:          '© Crown & Rose · All rights reserved.',

  // 2026-02 — "How we help writers" secondary marketing page. Every
  // key is admin-editable via Admin → Landing → Content → "How We
  // Help Writers page" group.
  hwh_hero_eyebrow:      'Home for Indie Authors',
  hwh_hero_title:        'How Crown & Rose helps writers build a business.',
  hwh_hero_body:
    'Multi-model AI drafting, a full royalty & marketing dashboard, direct-to-reader storefront, and OHAS D3 human-authorship certification — everything a serious indie novelist needs, under one roof.',
  hwh_ai_offerings_headline: 'AI assist for copywriters and creatives.',
  hwh_ai_offerings_body:
    'Writers are assisted by AI for copywriter-able creations; creatives may also find the Craft Multiplier useful for content in the bookstore under AI offerings.',
  // Newline-separated bullets so admins can add / remove rows without
  // touching JSON.
  hwh_ai_offerings_bullets:
    'Multi-model routing across Claude Sonnet, GPT-5, and Gemini 3.\n' +
    'Live character bible, plot beats, and chapter suggestions.\n' +
    'Full manuscript export to KDP, EPUB, and print-ready PDF.\n' +
    'Copyscape-backed plagiarism scans before you publish.',
  hwh_bottom_cta_title: 'Ready to try The Loom?',
  hwh_bottom_cta_body:
    'Free to get started — write your creation for free until January. Full manuscript access, character bibles, multi-model drafting, and direct-to-reader sales — no card required.',
  // Testimonials block — pipe-separated rows: "quote|author|book"
  // Empty string disables the strip.
  hwh_testimonials:
    'The Loom finally let me publish four books in a year without burning out — the character bible alone is worth every penny.|Marlowe Kenn|The Salt-Bright Tide|\n' +
    'I keep 85% of every sale and my readers can literally see how the book was made. That transparency changed my launch.|Iris Hollowell|Wolves at the Weft|\n' +
    'Direct-to-reader storefront + OHAS D3 provenance = trust I could never build on Amazon alone.|Beau Cassaday|Ledger of Small Hours|',
  // 2026-02 (moved from landing) · the "ready to start" invite banner
  // that now opens the How We Help Writers page, above its hero.
  hwh_invite_eyebrow:  'Hear it, then see how',
  hwh_invite_headline: 'Hear the result. Then start yours.',
  hwh_invite_body:
    'Multi-model AI drafting, direct-to-reader sales at 85%, OHAS D3 human-authorship certification, and a global community of indie novelists.',
  hwh_invite_button:   'Start free — write now',
};

export const LANDING_THEME_DEFAULTS = {
  font_family_key:        'system',
  base_font_size_px:      16,
  // Warm cream / parchment palette mirroring the app's Tailwind theme
  // tokens (`--background`, `--card`, `--primary`, `--primary-foreground`).
  // Until the admin saves any landing theme override, the page inherits
  // the same colour story as the rest of The Loom / Crown & Rose.
  background_color:       '#f7f5f2',
  card_background_color:  '#fdfcfb',
  accent_color:           '#c36d22',
  accent_foreground_color:'#fdfcfb',
};

export const LANDING_SEO_DEFAULTS = {
  title:           'Novel Writing Software for Authors — Crown & Rose',
  description:
    'Multi-model AI co-author (Claude, GPT, Gemini) for long-form ' +
    'fiction. Draft chapters, run character bibles, export manuscripts, ' +
    'sell in the store.',
  keywords:
    'company books, pro writer, AI novel writer, Claude Sonnet 4.5, ' +
    'GPT-5.2, Gemini Pro, long-form fiction, character bible, ' +
    'ebook store, scrivener alternative',
  og_image:        '',
  og_title:        '',
  og_description:  '',
  og_type:         'website',
  twitter_card:    'summary_large_image',
  canonical:       '',
  no_index:        false,
};

export function buildDefaultsDoc() {
  return {
    content: { ...LANDING_CONTENT_DEFAULTS },
    theme:   { ...LANDING_THEME_DEFAULTS },
    seo:     { ...LANDING_SEO_DEFAULTS },
  };
}

/* Ensure a Google Fonts <link> tag exists for the chosen family key.
 * Idempotent — adds at most one link per font key. */
export function ensureFontLoaded(key) {
  if (typeof document === 'undefined') return;
  const url = FONT_GFONT_URLS[key];
  if (!url) return;
  const existing = document.querySelector(`link[data-pw-font="${key}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.setAttribute('data-pw-font', key);
  document.head.appendChild(link);
}
